import React from "react"
import { getFavoriteOffers } from "../../../app/api/favoriteOfferApi"

type FavoriteOffersContextValue = {
    /** 状態取得が完了したか(未ログイン・エラーで確定した場合も true) */
    loaded: boolean
    /** キープ済みの offerId 集合(string 正規化済み) */
    favoriteOfferIds: Set<string>
}

const defaultValue: FavoriteOffersContextValue = {
    loaded: false,
    favoriteOfferIds: new Set(),
}

const FavoriteOffersContext =
    React.createContext<FavoriteOffersContextValue>(defaultValue)

/** Provider 外でも安全に動く(loaded:false, 空Set を返す) */
export const useFavoriteOffers = (): FavoriteOffersContextValue =>
    React.useContext(FavoriteOffersContext)

type FavoriteOffersProviderProps = {
    /** ページ上の全求人の offerId。マウント時に一括で状態取得する */
    offerIds: string[]
    children: React.ReactNode
}

/**
 * ページ内の「気になる」初期状態を一度だけ取得して配下の JobCard へ供給する。
 * - offerIds が空なら fetch しない(loaded:true, 空Set)
 * - not_logged_in / エラー / 通信失敗時も loaded:true, 空Set(全て未キープ表示)
 * - 未ログイン時にリダイレクトはしない(リダイレクトはクリック時のみ)
 */
export function FavoriteOffersProvider({
    offerIds,
    children,
}: FavoriteOffersProviderProps) {
    const [value, setValue] =
        React.useState<FavoriteOffersContextValue>(defaultValue)

    // 配列の中身が同じなら再取得しないよう、キーで依存を安定させる
    const offerIdsKey = offerIds.join(",")

    React.useEffect(() => {
        // 依存を offerIdsKey のみにするため、effect 内では key から id 配列を復元する。
        // これで「内容が同じ再レンダー」では再取得せず、参照する値と依存も一致する。
        const ids = offerIdsKey === "" ? [] : offerIdsKey.split(",")
        if (ids.length === 0) {
            setValue({ loaded: true, favoriteOfferIds: new Set() })
            return
        }

        let cancelled = false
        const resolveWith = (favoriteIds: Set<string>) => {
            if (!cancelled)
                setValue({ loaded: true, favoriteOfferIds: favoriteIds })
        }

        getFavoriteOffers(ids)
            .then(res => {
                if (res.status === "success" && res.data) {
                    resolveWith(
                        new Set(res.data.favorite_offer_ids.map(String))
                    )
                } else {
                    // not_logged_in / error はいずれも「未キープ」として確定
                    resolveWith(new Set())
                }
            })
            .catch(() => resolveWith(new Set()))

        return () => {
            cancelled = true
        }
    }, [offerIdsKey])

    return (
        <FavoriteOffersContext.Provider value={value}>
            {children}
        </FavoriteOffersContext.Provider>
    )
}
